import { useEffect, useState } from "react"
import { cn } from "@/lib/utils"

interface ProgressRingProps {
  progress: number // 0-100
  size?: 'sm' | 'md' | 'lg'
  strokeWidth?: number
  className?: string
  children?: React.ReactNode
  color?: 'primary' | 'accent' | 'secondary'
  animated?: boolean
  showPercentage?: boolean
}

export function ProgressRing({
  progress,
  size = 'md',
  strokeWidth,
  className,
  children,
  color = 'primary',
  animated = true,
  showPercentage = false
}: ProgressRingProps) {
  const [animatedProgress, setAnimatedProgress] = useState(0)

  // Size configurations
  const sizeConfig = {
    sm: { radius: 20, defaultStroke: 3, containerSize: 50 },
    md: { radius: 35, defaultStroke: 4, containerSize: 80 },
    lg: { radius: 50, defaultStroke: 5, containerSize: 110 }
  }

  const config = sizeConfig[size]
  const actualStrokeWidth = strokeWidth || config.defaultStroke
  const normalizedRadius = config.radius - actualStrokeWidth / 2
  const circumference = normalizedRadius * 2 * Math.PI

  // Color configurations
  const colorConfig = {
    primary: {
      stroke: 'hsl(var(--primary))',
      background: 'hsl(var(--primary) / 0.1)'
    },
    accent: {
      stroke: 'hsl(var(--accent))',
      background: 'hsl(var(--accent) / 0.1)'
    },
    secondary: {
      stroke: 'hsl(var(--secondary))',
      background: 'hsl(var(--secondary) / 0.1)'
    }
  }

  const colors = colorConfig[color]

  // Animate progress
  useEffect(() => {
    if (!animated) {
      setAnimatedProgress(progress)
      return
    }

    const timer = setTimeout(() => {
      setAnimatedProgress(progress)
    }, 100)

    return () => clearTimeout(timer)
  }, [progress, animated])

  const strokeDasharray = `${circumference} ${circumference}`
  const strokeDashoffset = circumference - (animatedProgress / 100) * circumference

  return (
    <div 
      className={cn("relative", className)}
      style={{ width: config.containerSize, height: config.containerSize }}
    >
      <svg
        height={config.containerSize}
        width={config.containerSize}
        className="transform -rotate-90"
      >
        {/* Background circle */}
        <circle
          stroke={colors.background}
          fill="transparent"
          strokeWidth={actualStrokeWidth}
          r={normalizedRadius}
          cx={config.containerSize / 2}
          cy={config.containerSize / 2}
        />
        
        {/* Progress circle */}
        <circle
          stroke={colors.stroke}
          fill="transparent"
          strokeWidth={actualStrokeWidth}
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          r={normalizedRadius}
          cx={config.containerSize / 2}
          cy={config.containerSize / 2}
          className={animated ? "transition-all duration-1000 ease-out" : ""}
        />
      </svg>

      {/* Content */}
      <div className="absolute inset-0 flex items-center justify-center">
        {showPercentage ? (
          <span className={`font-bold text-foreground ${size === 'sm' ? 'text-xs' : size === 'md' ? 'text-sm' : 'text-base'}`}>
            {Math.round(animatedProgress)}%
          </span>
        ) : (
          children
        )}
      </div>
    </div>
  )
}

interface MultiProgressRingProps {
  rings: Array<{
    progress: number
    color: 'primary' | 'accent' | 'secondary'
    strokeWidth?: number
  }>
  size?: 'sm' | 'md' | 'lg'
  className?: string
  children?: React.ReactNode
}

export function MultiProgressRing({
  rings,
  size = 'md',
  className,
  children
}: MultiProgressRingProps) {
  const sizeConfig = {
    sm: { baseRadius: 15, spacing: 4, containerSize: 50 },
    md: { baseRadius: 25, spacing: 6, containerSize: 80 },
    lg: { baseRadius: 35, spacing: 8, containerSize: 110 }
  }

  const config = sizeConfig[size]

  const colorConfig = {
    primary: 'hsl(var(--primary))',
    accent: 'hsl(var(--accent))',
    secondary: 'hsl(var(--secondary))'
  }

  return (
    <div 
      className={cn("relative", className)}
      style={{ width: config.containerSize, height: config.containerSize }}
    >
      <svg
        height={config.containerSize}
        width={config.containerSize}
        className="transform -rotate-90"
      >
        {rings.map((ring, index) => {
          const radius = config.baseRadius + (index * config.spacing)
          const strokeWidth = ring.strokeWidth || 3
          const normalizedRadius = radius - strokeWidth / 2
          const circumference = normalizedRadius * 2 * Math.PI
          const strokeDasharray = `${circumference} ${circumference}`
          const strokeDashoffset = circumference - (ring.progress / 100) * circumference

          return (
            <g key={index}>
              {/* Background */}
              <circle
                stroke={`${colorConfig[ring.color]}20`}
                fill="transparent"
                strokeWidth={strokeWidth}
                r={normalizedRadius}
                cx={config.containerSize / 2}
                cy={config.containerSize / 2}
              />
              
              {/* Progress */}
              <circle
                stroke={colorConfig[ring.color]}
                fill="transparent"
                strokeWidth={strokeWidth}
                strokeDasharray={strokeDasharray}
                strokeDashoffset={strokeDashoffset}
                strokeLinecap="round"
                r={normalizedRadius}
                cx={config.containerSize / 2}
                cy={config.containerSize / 2}
                className="transition-all duration-1000 ease-out"
              />
            </g>
          )
        })}
      </svg>

      {/* Content */}
      <div className="absolute inset-0 flex items-center justify-center">
        {children}
      </div>
    </div>
  )
}

// Weekly progress component specifically for movement tracking
interface WeeklyProgressProps {
  days: Array<{
    day: string
    completed: boolean
    intensity?: 'low' | 'medium' | 'high'
  }>
  className?: string
}

export function WeeklyProgress({ days, className }: WeeklyProgressProps) {
  const completedDays = days.filter(day => day.completed).length
  const progress = (completedDays / days.length) * 100

  const intensityColors = {
    low: 'hsl(var(--muted-foreground))',
    medium: 'hsl(var(--accent))', 
    high: 'hsl(var(--primary))'
  }

  return (
    <div className={cn("space-y-4", className)}>
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-foreground">This Week</h3>
        <span className="text-sm text-muted-foreground">
          {completedDays}/{days.length} days
        </span>
      </div>

      <div className="flex items-center justify-center">
        <ProgressRing 
          progress={progress}
          size="lg"
          color="accent"
          showPercentage
        />
      </div>

      <div className="flex justify-between gap-1">
        {days.map((day, index) => (
          <div key={index} className="flex flex-col items-center gap-2">
            <div 
              className={cn(
                "w-8 h-8 rounded-full border-2 flex items-center justify-center text-xs font-medium transition-all duration-200",
                day.completed 
                  ? "border-accent bg-accent text-accent-foreground" 
                  : "border-muted text-muted-foreground"
              )}
            >
              {day.completed ? "✓" : day.day.slice(0, 1)}
            </div>
            <span className="text-xs text-muted-foreground">
              {day.day.slice(0, 3)}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}